import { ref } from 'vue'
import { defineStore } from 'pinia';
import { signOut, auth, onAuthStateChanged } from 'src/api/firebase';
import api from 'src/api/index';
import type { CreateUser, User } from 'src/types/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const isAuthenticated = ref(false);
  const isInitialized = ref(false);
  const firebaseUid = ref<string | null>(null);
  const firebaseDisplayName = ref<string | null>(null);
  const firebaseEmail = ref<string | null>(null);
  const firebasePhotoURL = ref<string | null>(null);

  // Call once on app start — resolves after Firebase restores session
  const init = (): Promise<void> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
        unsubscribe();
        if (fbUser) {
          firebaseUid.value = fbUser.uid;
          firebaseDisplayName.value = fbUser.displayName;
          firebaseEmail.value = fbUser.email;
          firebasePhotoURL.value = fbUser.photoURL;
          isAuthenticated.value = true;
        }
        isInitialized.value = true;
        resolve();
      });
    });
  };
  const getUserDetails = async () => {
    if (firebaseUid.value == null) {
      return;
    }
    const u = await getUserById(firebaseUid.value);
    user.value = u;
    return user.value;
  }
  const loginWithEmail = async (email: string, password: string) => {
    const fbUser = await api.loginWithEmail(email, password);
    firebaseUid.value = fbUser.uid;
    firebaseDisplayName.value = fbUser.displayName;
    firebaseEmail.value = fbUser.email;
    firebasePhotoURL.value = fbUser.photoURL;
    isAuthenticated.value = true;
  };

  const loginWithGoogle = async () => {
    const fbUser = await api.loginWithGoogle();
    firebaseUid.value = fbUser.uid;
    firebaseDisplayName.value = fbUser.displayName;
    firebaseEmail.value = fbUser.email;
    firebasePhotoURL.value = fbUser.photoURL;
    isAuthenticated.value = true;
    try {
      const u = await getUserById(fbUser.uid);
      user.value = u;
    } catch {
      const createUserReq: CreateUser = {
        fn: fbUser.displayName?.split(' ')?.[0] || '',
        mn: '',
        ln: fbUser.displayName?.split(' ')?.[1] || '',
        email: fbUser.email || '',
        photoUrl: fbUser.photoURL || '',
        id: fbUser.uid
      }
      const res = await api.createUser(createUserReq)
      user.value = res.data;
    }
  };

  const getUserById = async (id: string) => {
    const u = await api.getUser(id)
    return u.data;
  }

  const logout = async () => {
    await signOut(auth);
    firebaseUid.value = null;
    firebaseDisplayName.value = null;
    firebaseEmail.value = null;
    firebasePhotoURL.value = null;
    isAuthenticated.value = false;
    user.value = null;
  };

  return {
    user, isAuthenticated, isInitialized,
    firebaseUid, firebaseDisplayName, firebaseEmail, firebasePhotoURL,
    init, loginWithEmail, loginWithGoogle, getUserById, logout, getUserDetails,
  };
});
